import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { Text } from "@nbs/ui";
import { NbsHorizontalBarChart } from "@/features/charts/NbsHorizontalBarChart";
import {
  storyEcoSeriesById,
  ecoBarsFromSeries,
  publishedLandCoverYears,
} from "@/features/story/data/services";
import { HubLandCoverMapsView } from "../components/HubLandCoverMapsView";
import { HubPageTitle } from "../components/HubPageTitle";
import { HUB_SERVICES_QUERY, HUB_SERVICES_QUERY_VARS } from "../api/documents";
import {
  LAND_COVER_MAP_YEARS,
  landCoverMapsForLandscape,
  type LandCoverMapYear,
} from "../data/land-cover-maps";
import { useHubScope } from "../state/HubScopeContext";

type ServicesViewMode = "graphs" | "maps";

function fmt(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 }).format(value);
}

function defaultMapYear(
  landscapeId: Parameters<typeof landCoverMapsForLandscape>[0],
  preferred: number,
): LandCoverMapYear {
  const available = landCoverMapsForLandscape(landscapeId);
  const hit = available.find((a) => a.year === preferred);
  if (hit) return hit.year;
  return (available[available.length - 1]?.year ?? 2025) as LandCoverMapYear;
}

export function HubServicesPage() {
  const { t } = useTranslation();
  const { landscapeId, landscapeLabel, period, queryVars } = useHubScope();
  const [viewMode, setViewMode] = useState<ServicesViewMode>("graphs");
  const [mapYear, setMapYear] = useState<LandCoverMapYear>(() =>
    defaultMapYear(landscapeId, period.yearTo),
  );

  useEffect(() => {
    const preferred = publishedLandCoverYears([period.yearTo], landscapeId)[0] ?? period.yearTo;
    setMapYear(defaultMapYear(landscapeId, preferred));
  }, [landscapeId, period.yearTo]);

  const yearOptions = useMemo(() => {
    const available = new Set(landCoverMapsForLandscape(landscapeId).map((a) => a.year));
    return publishedLandCoverYears(
      LAND_COVER_MAP_YEARS.filter((y) => available.has(y)),
      landscapeId,
    ) as LandCoverMapYear[];
  }, [landscapeId]);

  const { data, loading, error, refetch } = useQuery(HUB_SERVICES_QUERY, {
    variables: {
      territoryIds: queryVars.territoryIds,
      dateFrom: queryVars.dateFrom,
      dateTo: queryVars.dateTo,
      ecoFrom: "2014-01-01",
      ecoTo: "2014-12-31",
      ...HUB_SERVICES_QUERY_VARS,
    },
    fetchPolicy: "network-only",
    skip: viewMode !== "graphs",
  });

  const ecoById = useMemo(
    () => storyEcoSeriesById(data as Record<string, unknown> | undefined),
    [data],
  );
  const vet = ecoById["nbs-ind-eco-valeur-totale-vet"];
  const usage = ecoById["nbs-ind-eco-valeur-usage-totale"]?.points?.[0]?.value ?? null;
  const option = ecoById["nbs-ind-eco-valeur-option"]?.points?.[0]?.value ?? null;
  const nonUsage = ecoById["nbs-ind-eco-valeur-non-usage"]?.points?.[0]?.value ?? null;
  const vetValue = vet?.points?.[0]?.value ?? null;

  const approBars = useMemo(() => ecoBarsFromSeries(ecoById, "approvisionnement"), [ecoById]);
  const regBars = useMemo(() => ecoBarsFromSeries(ecoById, "regulation"), [ecoById]);
  const socioBars = useMemo(
    () => ecoBarsFromSeries(ecoById, "socio-culturel-spirituel"),
    [ecoById],
  );

  const synthesis = useMemo(() => {
    const rows: { label: string; value: number }[] = [];
    if (option != null) rows.push({ label: "Valeur d’option", value: option });
    if (usage != null) rows.push({ label: "Valeur d’usage", value: usage });
    if (nonUsage != null) rows.push({ label: "Valeur de non-usage", value: nonUsage });
    if (vetValue != null) rows.push({ label: "Valeur économique totale", value: vetValue });
    return rows;
  }, [option, usage, nonUsage, vetValue]);

  return (
    <div className="nbs-hub-page" data-testid="hub-services-page">
      <section className="nbs-hub-section">
        <HubPageTitle
          trailing={
            <div className="nbs-hub-services-toolbar">
              {viewMode === "maps" ? (
                <select
                  className="nbs-hub__plain-select"
                  data-testid="hub-services-year-select"
                  aria-label={t("hub.services.mapYearAria")}
                  value={mapYear}
                  onChange={(e) => setMapYear(Number(e.target.value) as LandCoverMapYear)}
                >
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>
                      {t("hub.services.occupationYear", { year: y })}
                    </option>
                  ))}
                </select>
              ) : null}
              <div
                className="nbs-hub-view-toggle"
                role="group"
                aria-label={t("hub.services.viewToggleAria")}
              >
                <button
                  type="button"
                  className={`nbs-hub-view-toggle__btn${viewMode === "graphs" ? " is-active" : ""}`}
                  aria-pressed={viewMode === "graphs"}
                  data-testid="hub-services-view-graphs"
                  onClick={() => setViewMode("graphs")}
                >
                  {t("hub.services.viewGraphs")}
                </button>
                <button
                  type="button"
                  className={`nbs-hub-view-toggle__btn${viewMode === "maps" ? " is-active" : ""}`}
                  aria-pressed={viewMode === "maps"}
                  data-testid="hub-services-view-maps"
                  onClick={() => setViewMode("maps")}
                >
                  {t("hub.services.viewMaps")}
                </button>
              </div>
            </div>
          }
        >
          {t("hub.services.title")}
        </HubPageTitle>

        {viewMode === "maps" ? (
          <HubLandCoverMapsView key={landscapeId} landscapeId={landscapeId} year={mapYear} />
        ) : (
          <div key={landscapeId}>
            {error ? (
              <div className="nbs-hub-banner nbs-hub-banner--danger" role="alert">
                <Text>Séries économiques / SE indisponibles.</Text>
                <button type="button" className="nbs-hub-btn" onClick={() => void refetch()}>
                  Réessayer
                </button>
              </div>
            ) : null}

            <div className="nbs-hub-graphs-layout">
              <div className="nbs-hub-service-grid">
                <article className="nbs-hub-card">
                  <header className="nbs-hub-card__head">
                    <h3>Approvisionnement</h3>
                    <p>Services publiés · {landscapeLabel}</p>
                  </header>
                  {approBars.length === 0 ? (
                    <p className="nbs-hub-muted">Pas de série pour ce volet.</p>
                  ) : (
                    <NbsHorizontalBarChart data={approBars} height={260} unit="$ US/ha/an" />
                  )}
                </article>
                <article className="nbs-hub-card">
                  <header className="nbs-hub-card__head">
                    <h3>Régulation</h3>
                    <p>Services publiés · {landscapeLabel}</p>
                  </header>
                  {regBars.length === 0 ? (
                    <p className="nbs-hub-muted">Pas de série pour ce volet.</p>
                  ) : (
                    <NbsHorizontalBarChart data={regBars} height={220} unit="$ US/ha/an" />
                  )}
                </article>
                <article className="nbs-hub-card">
                  <header className="nbs-hub-card__head">
                    <h3>Socio-culturel</h3>
                    <p>Services publiés · {landscapeLabel}</p>
                  </header>
                  {socioBars.length === 0 ? (
                    <p className="nbs-hub-muted">Pas de série pour ce volet.</p>
                  ) : (
                    <NbsHorizontalBarChart data={socioBars} height={180} unit="$ US/ha/an" />
                  )}
                </article>
              </div>

              <aside className="nbs-hub-card nbs-hub-synth" data-testid="hub-services-synth">
                <header className="nbs-hub-card__head">
                  <h3>{t("hub.services.synthTitle")}</h3>
                </header>
                {loading && synthesis.length === 0 ? (
                  <p className="nbs-hub-muted">Chargement…</p>
                ) : synthesis.length === 0 ? (
                  <p className="nbs-hub-muted">Aucune valeur économique publiée.</p>
                ) : (
                  <dl className="nbs-hub-synth__list">
                    {synthesis.map((row) => (
                      <div key={row.label} className="nbs-hub-synth__row">
                        <dt>{row.label}</dt>
                        <dd>
                          {fmt(row.value)}
                          <small> {vet?.unit ?? "$ US/ha/an"}</small>
                        </dd>
                      </div>
                    ))}
                  </dl>
                )}
                <p className="nbs-hub-synth__note">réf. 2014</p>
              </aside>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
